import { useState, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Pencil, Save, X, Info, ShieldCheck, Lock } from "lucide-react";

const inputCls = "h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white placeholder:text-neutral-600 focus-visible:border-[#E30613] focus-visible:ring-1 focus-visible:ring-[#E30613]";

const EDITABLE = ["business_name", "contact_name", "phone", "business_email", "website",
  "address_line1", "address_line2", "city", "state", "zip_code", "country"];
const COMPLETENESS_FIELDS = ["business_name", "contact_name", "phone", "business_email", "address_line1", "city", "state", "zip_code"];

function ReadField({ label, value, icon: Icon }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-[#0A0A0A] p-4">
      <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-600">{Icon && <Icon className="h-3 w-3" />}{label}</p>
      <p className="mt-1.5 text-sm text-white">{value || "—"}</p>
    </div>
  );
}

function EditField({ label, name, form, set, type = "text", placeholder, required }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-neutral-300">{label}{required && <span className="ml-1 text-[#FF6069]">*</span>}</Label>
      <Input type={type} value={form[name] || ""} onChange={(e) => set(name, e.target.value)} placeholder={placeholder} className={inputCls} data-testid={`profile-${name}`} />
    </div>
  );
}

export default function Profile() {
  const { user, setUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const startEdit = () => {
    const seed = {};
    EDITABLE.forEach((k) => { seed[k] = user?.[k] || ""; });
    setForm(seed);
    setEditing(true);
  };
  const cancel = () => { setEditing(false); setForm({}); };

  const source = editing ? form : (user || {});
  const completeness = useMemo(() => {
    const filled = COMPLETENESS_FIELDS.filter((k) => (source[k] || "").toString().trim()).length;
    return Math.round((filled / COMPLETENESS_FIELDS.length) * 100);
  }, [source]);

  const save = async () => {
    if (!(form.business_name || "").trim()) { toast.error("Business name is required"); return; }
    if (!(form.contact_name || "").trim()) { toast.error("Contact name is required"); return; }
    setSaving(true);
    try {
      const payload = {};
      EDITABLE.forEach((k) => { payload[k] = (form[k] || "").trim(); });
      const { data } = await api.put("/profile", payload);
      setUser(data);
      setEditing(false);
      toast.success("Business profile updated successfully.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save profile. Please try again.");
    } finally { setSaving(false); }
  };

  const addressLine = [user?.address_line1, user?.address_line2, [user?.city, user?.state].filter(Boolean).join(", "), user?.zip_code, user?.country].filter(Boolean).join(", ") || user?.address;

  return (
    <DashboardLayout title="Business Profile">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">Account</p>
          <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-white">Business Profile</h2>
          <p className="mt-1 text-sm text-neutral-500">Manage your business and customer-facing contact information.</p>
        </div>
        {!editing ? (
          <button onClick={startEdit} className="inline-flex items-center gap-2 self-start rounded-lg bg-[#E30613] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#FF1C2A]" data-testid="edit-profile-btn"><Pencil className="h-4 w-4" /> Edit Profile</button>
        ) : (
          <div className="flex gap-2 self-start">
            <button onClick={cancel} disabled={saving} className="inline-flex items-center gap-2 rounded-lg border border-white/[0.1] px-4 py-2.5 text-sm font-medium text-neutral-300 hover:bg-white/[0.05]" data-testid="cancel-profile-btn"><X className="h-4 w-4" /> Cancel</button>
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-60" data-testid="save-profile-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes</button>
          </div>
        )}
      </div>

      <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-[#E30613]/15 bg-[#E30613]/[0.04] p-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex items-start gap-2 text-xs text-neutral-400"><Info className="mt-0.5 h-4 w-4 shrink-0 text-[#FF6069]" /> Keep your business information current. Your contact information may appear on installation certificates and customer communications.</p>
        <div className="flex shrink-0 items-center gap-3" data-testid="profile-completeness">
          <div className="h-2 w-32 overflow-hidden rounded-full bg-white/[0.08]"><div className="h-full rounded-full bg-[#E30613] transition-all" style={{ width: `${completeness}%` }} /></div>
          <span className="text-sm font-semibold text-white">{completeness}%</span>
        </div>
      </div>
      {completeness < 100 && !editing && (
        <p className="mb-6 -mt-3 text-xs text-neutral-500">Tip: complete your customer-facing details so customers can always reach you.</p>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <section className="rounded-2xl border border-white/[0.06] bg-[#141414] p-6" data-testid="section-business">
            <h3 className="mb-4 font-display text-base font-semibold text-white">Business Information</h3>
            {editing ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <EditField label="Business Name" name="business_name" form={form} set={set} required />
                <EditField label="Contact Name" name="contact_name" form={form} set={set} required />
                <EditField label="Business Phone" name="phone" form={form} set={set} placeholder="+1 (555) 123-4567" />
                <EditField label="Customer-Facing Email" name="business_email" form={form} set={set} type="email" placeholder="service@yourbusiness.com" />
                <div className="sm:col-span-2"><EditField label="Website" name="website" form={form} set={set} placeholder="https://yourbusiness.com" /></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <ReadField label="Business Name" value={user?.business_name} />
                <ReadField label="Contact Name" value={user?.contact_name} />
                <ReadField label="Business Phone" value={user?.phone} />
                <ReadField label="Customer-Facing Email" value={user?.business_email} />
                <div className="sm:col-span-2"><ReadField label="Website" value={user?.website} /></div>
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-white/[0.06] bg-[#141414] p-6" data-testid="section-address">
            <h3 className="mb-4 font-display text-base font-semibold text-white">Business Address</h3>
            {editing ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2"><EditField label="Address Line 1" name="address_line1" form={form} set={set} /></div>
                <div className="sm:col-span-2"><EditField label="Address Line 2" name="address_line2" form={form} set={set} placeholder="Suite, unit, etc. (optional)" /></div>
                <EditField label="City" name="city" form={form} set={set} />
                <EditField label="State / Province" name="state" form={form} set={set} />
                <EditField label="ZIP / Postal Code" name="zip_code" form={form} set={set} />
                <EditField label="Country" name="country" form={form} set={set} />
              </div>
            ) : (
              <ReadField label="Address" value={addressLine} />
            )}
          </section>
        </div>

        <aside className="space-y-4">
          <section className="rounded-2xl border border-white/[0.06] bg-[#141414] p-6" data-testid="section-account">
            <h3 className="mb-1 flex items-center gap-2 font-display text-base font-semibold text-white"><ShieldCheck className="h-4 w-4 text-[#FF6069]" /> Account</h3>
            <p className="mb-4 flex items-center gap-1 text-[11px] text-neutral-600"><Lock className="h-3 w-3" /> Managed by SGBINOXE</p>
            <div className="space-y-3">
              <ReadField label="Login Email" value={user?.email} />
              <ReadField label="Account Status" value={user?.status?.replace(/_/g, " ")} />
              <ReadField label="Certificate Credits" value={user?.certificate_credits} />
              <ReadField label="Member Since" value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"} />
            </div>
            <p className="mt-4 text-[11px] leading-relaxed text-neutral-600">Your login email is separate from your customer-facing email and can't be changed here. Contact SGBINOXE to update account settings, credits or status.</p>
          </section>
        </aside>
      </div>
    </DashboardLayout>
  );
}
